import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Inbox } from 'lucide-react'
import toast from 'react-hot-toast'
import commissionService from '@/services/commissionService'
import PageContainer from '@/components/common/PageContainer'
import Pagination from '@/components/common/Pagination'
import DataTable from '@/components/common/DataTable'
import { EmptyState } from '@/components/common/EmptyState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

const statusVariants = {
  pending: 'warning',
  approved: 'default',
  processing: 'default',
  completed: 'success',
  failed: 'danger',
  cancelled: 'secondary',
}

const statusLabels = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  processing: 'Procesando',
  completed: 'Completado',
  failed: 'Fallido',
  cancelled: 'Cancelado',
}

export default function PayoutHistory() {
  const navigate = useNavigate()
  const [payouts, setPayouts] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)

  useEffect(() => {
    fetchPayouts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage])

  const fetchPayouts = async () => {
    setLoading(true)
    try {
      const data = await commissionService.getPayouts(
        {},
        { page: page + 1, limit: rowsPerPage }
      )
      setPayouts(data.payouts || [])
      setTotal(data.pagination?.total || 0)
    } catch {
      toast.error('Error al cargar historial de pagos')
    } finally {
      setLoading(false)
    }
  }

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('es-ES', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '—'

  // Una sola definición para la tabla y las tarjetas del teléfono.
  const columnas = [
    {
      clave: 'monto', titulo: 'Monto', prioridad: 'titulo',
      celda: (p) => (
        <div>
          <p className="font-mono text-caption text-subtle md:hidden">{p._id.slice(-8).toUpperCase()}</p>
          <p className="font-semibold tabular-nums text-foreground max-md:font-display max-md:text-h3">{commissionService.formatCurrency(p.amount)}</p>
        </div>
      ),
    },
    { clave: 'id', titulo: 'ID', prioridad: 'oculta', celda: (p) => <span className="font-mono text-caption text-subtle">{p._id.slice(-8).toUpperCase()}</span> },
    { clave: 'comisiones', titulo: 'Comisiones', prioridad: 'meta', celda: (p) => <span className="tabular-nums text-foreground">{p.commissionCount || p.commissionIds?.length || 0}</span> },
    {
      clave: 'estado', titulo: 'Estado', prioridad: 'valor',
      celda: (p) => <Badge variant={statusVariants[p.status] || 'secondary'}>{statusLabels[p.status] || p.status}</Badge>,
    },
    { clave: 'solicitud', titulo: 'Solicitud', prioridad: 'meta', celda: (p) => <span className="text-foreground">{fmtDate(p.createdAt)}</span> },
    { clave: 'pago', titulo: 'Pago', prioridad: 'meta', celda: (p) => <span className="text-foreground">{fmtDate(p.paidAt)}</span> },
    {
      clave: 'referencia', titulo: 'Referencia', prioridad: 'meta',
      celda: (p) => <span className="break-all font-mono text-caption text-foreground">{p.paymentDetails?.transactionReference || '—'}</span>,
    },
  ]

  return (
    <PageContainer>
      <div className="flex items-start gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/commissions')}
          aria-label="Volver"
        >
          <ArrowLeft size={18} />
        </Button>
        <div className="min-w-0">
          <h2 className="font-display text-h1 font-semibold leading-tight text-foreground">
            Historial de pagos
          </h2>
          <p className="mt-0.5 text-body-sm text-subtle">
            Todas tus solicitudes de pago
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <DataTable
          etiquetaTabla="Historial de pagos"
          columnas={columnas}
          filas={payouts}
          claveDeFila={(p) => p._id}
          cargando={loading}
          tarjetasEnMovil
          className="bg-card"
          vacio={<EmptyState icono={<Inbox size={24} />} titulo="Sin pagos registrados" descripcion="Cuando pidas un pago de tus comisiones aprobadas, lo verás aquí con su estado." />}
        />

        {!loading && payouts.length > 0 && (
          <Pagination
            className="rounded-lg border border-line bg-card"
            page={page}
            pageSize={rowsPerPage}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setRowsPerPage(s)
              setPage(0)
            }}
          />
        )}
      </div>
    </PageContainer>
  )
}
